import { reportError } from "@/utils/reportError";
import { supabase } from '@/utils/supabase';
import { getCached, setCached } from '@/utils/cache';
import { useCallback, useEffect, useState } from 'react';

export interface UserScores {
  id: string;
  userId: string;
  totalPoints: number;
  weeklyPoints: number;
  monthlyPoints: number;
  streakDays: number;
  bestStreak: number;
  totalActionsCompleted: number;
  daysWithObjectiveMet: number;
  bonusPoints: number;
  lastActionDate: string | null;
}

function mapScoresRow(data: any): UserScores {
  return {
    id: data.id,
    userId: data.user_id,
    totalPoints: data.total_points,
    weeklyPoints: data.weekly_points,
    monthlyPoints: data.monthly_points,
    streakDays: data.streak_days,
    bestStreak: data.best_streak,
    totalActionsCompleted: data.total_actions_completed,
    daysWithObjectiveMet: data.days_with_objective_met,
    bonusPoints: data.bonus_points,
    lastActionDate: data.last_action_date,
  };
}

export function useUserScores() {
  const [scores, setScores] = useState<UserScores | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchScores = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) {
        setLoading(false);
        return;
      }

      // Stale-while-revalidate: hydrate from cache for instant header points
      const cacheKey = `scores:${user.id}`;
      const cached = await getCached<UserScores>(cacheKey);
      if (cached) {
        setScores(cached);
        setLoading(false);
      } else {
        setLoading(true);
      }

      const { data, error: fetchError } = await supabase
        .from('user_scores')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      if (data) {
        const next = mapScoresRow(data);
        setScores(next);
        setCached(`scores:${user.id}`, next);
      }
    } catch (err: any) {
      reportError(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScores();
  }, [fetchScores]);

  // Incrément atomique côté serveur (RPC `increment_user_points`) : plus de
  // read-modify-write basé sur l'état React. L'ancien UPDATE absolu écrasait
  // les points gagnés sur un autre écran/appareil quand l'état local était
  // périmé (cache SWR, hooks instanciés par onglet), et perdait silencieusement
  // les points quand `scores` n'était pas encore chargé.
  const addPoints = async (points: number, isBonus: boolean = false): Promise<boolean> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return false;

      const { data, error: rpcError } = await supabase
        .rpc('increment_user_points', { p_points: points, p_bonus: isBonus })
        .single();

      if (rpcError) throw rpcError;
      if (!data) return false;

      const next = mapScoresRow(data);
      setScores(next);
      setCached(`scores:${user.id}`, next);
      return true;
    } catch (err: any) {
      reportError(err);
      setError(err.message);
      return false;
    }
  };

  // Transition de streak côté serveur (RPC `apply_streak_update`) : la même
  // logique que utils/scoring.ts computeStreakUpdate, mais calculée sur la
  // ligne DB verrouillée plutôt que sur l'état local.
  const updateStreak = async (objectiveMet: boolean): Promise<void> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      const { data, error: rpcError } = await supabase
        .rpc('apply_streak_update', { p_objective_met: objectiveMet })
        .maybeSingle();

      if (rpcError) throw rpcError;
      if (!data) return;

      const next = mapScoresRow(data);
      setScores(next);
      setCached(`scores:${user.id}`, next);
    } catch (err: any) {
      reportError(err);
    }
  };

  return {
    scores,
    loading,
    error,
    addPoints,
    updateStreak,
    refetch: fetchScores,
  };
}
